import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  Lock,
  HelpCircle,
  Briefcase,
  AlertCircle,
  Clock,
  Infinity as InfinityIcon,
  ArrowRight,
  ShieldCheck,
  FileText,
  Coins,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useGetCreditsBalance } from "@/hooks/api/query/useGetCreditsBalance";
import { envConfig } from "@/lib/config";
import { cn } from "@/lib/utils";
import {
  ACTION_DISPLAY_CONFIG,
  type BillableActionType,
} from "@/constants/credit";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { getRedirectUrlForAction } from "@/utils/paywallRedirects";
import { isPackageRecommended } from "@/utils/packageRecommendation";
import { useAuth } from "../../contexts/AuthContext";
import { useGetCheckoutSession } from "../../hooks/api/query/useGetCheckoutSession";
import { useGetProducts } from "../../hooks/api/query/useGetProducts";
import { useIsPaymentsEnabled } from "../../hooks/api/query/useIsPaymentsEnabled";
import { useWizardSession } from "../../hooks/useWizardSession";
import { queryKeys } from "../../lib/queryClient";
import { PaymentsDisabledContent } from "./PaymentsDisabledContent";
import { JourneyCostCalculator } from "./JourneyCostCalculator";

type PaywallDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType?: BillableActionType;
  mode?: "paywall" | "buy";
  onPurchase?: (packageId: string) => void;
};

const stripePromise = envConfig.STRIPE.PUBLIC_KEY
  ? loadStripe(envConfig.STRIPE.PUBLIC_KEY)
  : null;

const TrustBadge = ({
  icon: Icon,
  text,
}: {
  icon: React.ElementType;
  text: string;
}) => (
  <div className="flex items-center gap-2 text-xs text-ds-text-500 font-medium font-mono bg-ds-surface-50 px-3 py-1.5 rounded-full border border-ds-border-200">
    <Icon className="w-3.5 h-3.5 text-ds-text-400" />
    <span>{text}</span>
  </div>
);

function formatCurrencySymbol(currency: string): string {
  const upper = currency.toUpperCase();
  if (upper === "EUR") return "EUR";
  return upper;
}

export function PaywallDialog({
  open,
  onOpenChange,
  actionType,
  mode = "paywall",
  onPurchase,
}: PaywallDialogProps) {
  const isBuyMode = mode === "buy";
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [journeyHighlightedCredits, setJourneyHighlightedCredits] = useState<
    number | null
  >(null);
  const { sessionId } = useWizardSession();

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setClientSecret(null);
    onOpenChange(nextOpen);
  };

  const { user } = useAuth();
  const { isPaymentsEnabled } = useIsPaymentsEnabled();

  const { data: creditsData } = useGetCreditsBalance({
    enabled: !!user && open,
    queryKey: queryKeys.credits.balance(),
  });

  const { data: productsData, isLoading: isLoadingProducts } = useGetProducts({
    enabled: !!user && open,
  });

  const { mutate: getCheckoutSession } = useGetCheckoutSession();

  const { data: actionCreditsData } = useCheckActionCredits(
    {
      session_id: sessionId ?? "",
      action_type: actionType as BillableActionType,
    },
    !isBuyMode && !!user && open && !!sessionId && !!actionType
  );

  const action = actionType ? ACTION_DISPLAY_CONFIG[actionType] : null;
  const currentCredits =
    creditsData?.credits ?? actionCreditsData?.current_balance ?? 0;
  const progressTotal = actionCreditsData?.required ?? 0;
  const creditsNeeded = Math.max(0, progressTotal - currentCredits);

  const products = productsData?.products ?? [];
  const hasProducts = !isLoadingProducts && products.length > 0;

  const handlePurchase = (priceId: string) => {
    if (onPurchase) onPurchase(priceId);
    if (!priceId) return;
    const redirectUrl =
      isBuyMode || !actionType
        ? "/profil"
        : getRedirectUrlForAction(actionType, sessionId);
    getCheckoutSession(
      { priceId, sessionId, redirectUrl },
      {
        onSuccess: (data) => {
          if (data?.checkoutSessionId) setClientSecret(data.checkoutSessionId);
        },
        onError: (error) => {
          console.error("Failed to create checkout session:", error);
        },
      }
    );
  };

  if (!isPaymentsEnabled) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby="paywall-dialog-description"
          className="max-w-md rounded-2xl shadow-2xl"
        >
          <DialogTitle className="sr-only">Plăți indisponibile</DialogTitle>
          <PaymentsDisabledContent onClose={() => handleOpenChange(false)} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby="paywall-dialog-description"
        className={cn(
          "max-w-[1100px] rounded-2xl shadow-2xl",
          clientSecret
            ? "p-0 gap-0 overflow-hidden"
            : "overflow-auto max-h-[92vh]"
        )}
      >
        <DialogTitle className="sr-only">
          {isBuyMode ? "Cumpără credite" : "Deblocare acțiune"}
        </DialogTitle>
        {clientSecret ? (
          <div className="max-h-[90vh] overflow-y-auto">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                clientSecret,
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        ) : (
          <>
            <div className="flex flex-col md:flex-row p-0">
              {/* LEFT PANEL: Context & Motivation (35% width) */}
              <div className="w-full md:w-[35%] bg-slate-50 border-r border-slate-100 p-4 md:p-8 flex flex-col justify-between">
                <div>
                  {/* Header Badge */}
                  <div className="flex items-center gap-2.5 text-blue-700 mb-5 md:mb-8">
                    <div className="p-2.5 bg-blue-100 rounded-xl">
                      {isBuyMode ? (
                        <Coins className="w-5 h-5" />
                      ) : (
                        <Lock className="w-5 h-5" />
                      )}
                    </div>
                    <span className="font-bold text-xs md:text-sm uppercase tracking-wider">
                      {isBuyMode ? "Cumpără Credite" : "Deblocare Acțiune"}
                    </span>
                  </div>

                  {/* Main Title */}
                  <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">
                    {isBuyMode ? "Reîncarcă-ți balanța" : action?.name}
                  </h2>
                  <p className="text-slate-600 text-sm mb-2 md:mb-6">
                    {isBuyMode
                      ? "Alege un pachet de credite pentru a avea acces nelimitat la toate acțiunile platformei."
                      : action?.description}
                  </p>

                  {/* Deficit note */}
                  {!isBuyMode && creditsNeeded > 0 && (
                    <div className="flex items-start gap-2.5 text-sm text-amber-700 bg-amber-50 p-3 md:p-4 rounded-xl border border-amber-200 mb-4 md:mb-7">
                      <HelpCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <p className="font-medium leading-snug">
                        Îți mai lipsesc{" "}
                        <span className="font-bold">
                          {creditsNeeded} credite
                        </span>
                        . Alege un pachet din dreapta pentru a continua.{" "}
                        <ArrowRight className="w-3.5 h-3.5 inline-block" />
                      </p>
                    </div>
                  )}

                  {/* Benefits */}
                  <div className="space-y-1">
                    <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wider border-b border-slate-200 pb-2 mb-3 md:mb-4">
                      Beneficiile pachetului de credite
                    </h3>
                    <div className="flex items-center gap-3 py-2 md:py-2.5 border-b border-slate-100">
                      <div className="p-2 bg-blue-50 rounded-lg shrink-0">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        Gestionează mai multe companii
                      </span>
                    </div>
                    <div className="flex items-center gap-3 py-2 md:py-2.5 border-b border-slate-100">
                      <div className="p-2 bg-orange-50 rounded-lg shrink-0">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        Analiză de Risc
                      </span>
                    </div>
                    <div className="flex items-center gap-3 py-2 md:py-2.5 border-b border-slate-100">
                      <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                        <Clock className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        Evaluare Maturitate
                      </span>
                    </div>
                    <div className="flex items-center gap-3 py-2 md:py-2.5 border-b border-slate-100">
                      <div className="p-2 bg-indigo-50 rounded-lg shrink-0">
                        <FileText className="w-4 h-4 text-indigo-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        Plan de Remediere
                      </span>
                    </div>
                    <div className="flex items-center gap-3 py-2 md:py-2.5">
                      <div className="p-2 bg-green-50 rounded-lg shrink-0">
                        <InfinityIcon className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        Valabilitate nelimitată
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL: Pricing (65% width) */}
              <div className="w-full md:w-[65%] p-4 md:p-8 bg-white">
                {/* Header */}
                <div className="text-center mb-3 md:mb-5">
                  <h2 className="text-lg md:text-xl font-bold text-slate-900">
                    Alege pachetul de credite
                  </h2>
                  <p className="text-slate-500 text-sm mt-1">
                    Toate prețurile includ TVA. Factura se emite automat.
                  </p>
                </div>

                {/* Pricing Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6">
                  {isLoadingProducts &&
                    Array.from({ length: 4 }).map((_, idx) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed skeleton placeholders
                        key={`pkg-skeleton-${idx}`}
                        className="relative rounded-xl p-4 md:p-5 flex flex-col border border-slate-200 bg-white animate-pulse"
                      >
                        <div className="mb-3 h-5" />
                        <div className="h-7 bg-slate-100 rounded w-24 mb-2" />
                        <div className="h-4 bg-slate-100 rounded w-16 mb-1" />
                        <div className="h-6 bg-slate-100 rounded w-20 mb-2" />
                        <div className="h-4 bg-slate-100 rounded w-20 mb-2" />
                        <div className="h-9 bg-slate-100 rounded mt-auto" />
                      </div>
                    ))}

                  {hasProducts &&
                    products.map((product) => {
                      const price = product.unitAmount / 100;
                      const originalPrice =
                        product.originalAmount != null
                          ? product.originalAmount / 100
                          : null;
                      const savings =
                        originalPrice != null ? originalPrice - price : 0;
                      const priceLabel = price.toFixed(2);
                      const originalPriceLabel =
                        originalPrice != null ? originalPrice.toFixed(2) : null;
                      const savingsLabel = savings.toFixed(2);
                      const perCredit = (price / product.credits).toFixed(2);
                      const currencyLabel = formatCurrencySymbol(
                        product.currency
                      );
                      const recommended = isPackageRecommended(
                        product,
                        journeyHighlightedCredits
                      );

                      return (
                        <div
                          key={product.id}
                          className={cn(
                            "relative rounded-xl p-4 md:p-5 flex flex-col transition-all duration-200",
                            recommended
                              ? "border-2 border-green-500 bg-green-50/30 shadow-sm shadow-green-100"
                              : "border border-slate-200 bg-white hover:border-blue-300 hover:shadow-md"
                          )}
                        >
                          <div className="mb-3 h-5 flex items-center justify-center">
                            {recommended && (
                              <span className="text-[10px] font-bold font-mono text-green-700 bg-green-100 px-2 py-0.5 rounded-full uppercase tracking-wider leading-none">
                                RECOMANDAT
                              </span>
                            )}
                          </div>

                          <div className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-none mb-1 text-center">
                            {product.credits}
                            <span className="text-sm font-semibold text-slate-500 ml-1.5">
                              credite
                            </span>
                          </div>
                          <div className="text-[10px] md:text-[11px] font-mono font-medium text-slate-500 tracking-wide text-center mb-2">
                            {product.name}
                          </div>

                          {originalPriceLabel != null && (
                            <div className="text-xs text-orange-500 font-semibold line-through mt-3 mb-0.5 text-center">
                              {originalPriceLabel} {currencyLabel}
                            </div>
                          )}

                          <div
                            className={cn(
                              "text-xl md:text-2xl font-extrabold text-slate-900 leading-none mb-1.5 md:mb-2 text-center whitespace-nowrap",
                              originalPrice == null && "mt-3"
                            )}
                          >
                            {priceLabel}{" "}
                            <span className="text-sm font-medium text-slate-500">
                              {currencyLabel}
                            </span>
                          </div>

                          {originalPrice != null && savings > 0 && (
                            <div className="text-[10px] font-bold font-mono text-green-700 bg-green-50 border border-green-100 rounded-full px-2 py-0.5 mb-1.5 md:mb-2 self-center">
                              Economisești {savingsLabel} {currencyLabel}
                            </div>
                          )}

                          <div className="text-[10px] md:text-[11px] text-slate-400 mb-3 md:mb-4 text-center">
                            {perCredit} {currencyLabel}/credit
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePurchase(product.priceId);
                            }}
                            className={cn(
                              "mt-auto w-full py-2 md:py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center",
                              recommended
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                            )}
                          >
                            Alege pachet
                          </button>
                        </div>
                      );
                    })}
                </div>

                {/* Journey Cost Calculator */}
                {!isBuyMode && (
                  <JourneyCostCalculator
                    sessionId={sessionId}
                    highlightedPackageCredits={journeyHighlightedCredits}
                    onRecommendedPackage={setJourneyHighlightedCredits}
                  />
                )}

                {/* Current balance (buy mode) */}
                {isBuyMode && (
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-600 py-3 bg-slate-50 rounded-xl border border-slate-100">
                    <Coins className="w-4 h-4 text-primary" />
                    <span>
                      Balanță curentă:{" "}
                      <span className="font-bold text-slate-900">
                        {currentCredits.toLocaleString()} credite
                      </span>
                    </span>
                  </div>
                )}

                {/* Trust Footer */}
                <div className="flex flex-wrap justify-center gap-2 md:gap-3 pt-3 md:pt-4 mt-3 md:mt-4 border-t border-slate-100">
                  <TrustBadge icon={InfinityIcon} text="Creditele nu expiră" />
                  <TrustBadge icon={FileText} text="Factură fiscală automată" />
                  <TrustBadge
                    icon={ShieldCheck}
                    text="Plată securizată prin Stripe"
                  />
                </div>
              </div>
            </div>

            {/* Consultant banner - full-width sticky bottom */}
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 md:px-8 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0">
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">
                  Ai nevoie de ajutor?{" "}
                  <Link
                    to="/contact"
                    onClick={() => handleOpenChange(false)}
                    className="text-blue-600 font-medium hover:underline"
                  >
                    Contactează suportul
                  </Link>
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-xs text-slate-500">
                  Ești Consultant sau Avocat?{" "}
                  <Link
                    to="/contact"
                    onClick={() => handleOpenChange(false)}
                    className="text-blue-600 font-medium hover:underline"
                  >
                    Discută despre Parteneriat
                  </Link>
                </span>
              </div>
            </div>
          </>
        )}

        {/* Shimmer Animation CSS */}
        <style>{`
					@keyframes shimmer {
						100% {
							transform: translateX(100%);
						}
					}
					.animate-shimmer {
						animation: shimmer 2s infinite;
					}
				`}</style>
      </DialogContent>
    </Dialog>
  );
}
